/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect } from 'react';
import { components } from 'generated/payment-initialisation/schema';
import useForm from 'hooks/useForm';
import useSwitchAccount from 'hooks/useSwitchAccount';
import { TCountries } from 'models/countries';
import { RemittersService } from 'services/Remitters/remitters.service';
import t from 'utils/translationHelper';
import * as yup from 'yup';

import { CurrencyList } from '@alpha/bene-dtos';
import { ActionButton } from '@alpha/ui-lib/ui/button/ActionButton';
import { Drawer } from '@alpha/ui-lib/ui/Drawer';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faSave } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationCircle, faPaperPlane, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import BankDetailsSection from './BankDetailsSection';
import BasicInformation from './BasicInformation';
import useStyles from './RemitterDrawer.styles';
import mapToRemitterCreateRequest, { mapRemitterResponseToFormValues, mapToRemitterUpdateRequest } from './remitterDrawerMapper';

export interface IRemitterFormValues {
  // Basic Information
  partyType: 1 | 3;
  remitterName: string;
  country: string;
  currency: string;
  // Bank Details
  bankName: string;
  nationalBankCode: string;
  accountNumber: string;
  iban: string;
  swiftBic: string;
  // Bank Address
  bankAddressLine1: string;
  bankCity: string;
  bankRegion: string;
  bankPostalCode: string;
  bankAddressCountryCode: string;
}

const initialValues: IRemitterFormValues = {
  partyType: 1,
  remitterName: '',
  country: '',
  currency: '',
  bankName: '',
  nationalBankCode: '',
  accountNumber: '',
  iban: '',
  swiftBic: '',
  bankAddressLine1: '',
  bankCity: '',
  bankRegion: '',
  bankPostalCode: '',
  bankAddressCountryCode: '',
};

const validation = {
  remitterName: yup.string().required(t('invalid_remitter_name')).matches(/^[A-Za-z0-9 /\-?:.]+$/, t('invalid_remitter_name')),
  country: yup.string().required(t('country_required')),
  currency: yup.string().required(t('currency_required')),
  bankName: yup.string().required(t('invalid_bank_name')).matches(/^[A-Za-z0-9 /\-?:.]+$/, t('invalid_bank_name')),
  iban: yup.string().required(t('iban_required')).matches(
    /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/,
    t('invalid_iban_format'),
  ),
  swiftBic: yup.string().required(t('swift_bic_required')).matches(
    /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
    t('invalid_swift_bic_format'),
  ),
  bankAddressLine1: yup.string().required(t('address_line_1_required')).matches(/^[A-Za-z0-9 /\-?,:.]+$/, t('invalid_address_line_1')),
  bankCity: yup.string().required(t('city_required')).matches(/^[A-Za-z0-9 /\-?:.]+$/, t('invalid_city')),
  bankRegion: yup.string().required(t('state_required')).matches(/^[A-Za-z0-9 /\-?:.]+$/, t('invalid_state')),
  bankPostalCode: yup.string().required(t('zip_required')).matches(/^[A-Za-z0-9 /\-?:.]+$/, t('invalid_zip')),
  bankAddressCountryCode: yup.string().required(t('country_code_required')),
};

interface IRemitterDrawerProps {
    open: boolean;
    handleClose: () => void;
    refetchRemitters: () => Promise<void>;
    editRemitterValues?: components['schemas']['RemitterResponse'];
    countries: TCountries;
    currencies: CurrencyList;
}
const RemitterDrawer: React.FC<IRemitterDrawerProps> = ({
  open, handleClose, refetchRemitters, editRemitterValues, countries, currencies,
}) => {
  const styles = useStyles();
  const { currentAccount } = useSwitchAccount();
  const handleSubmit = async (values: IRemitterFormValues) => {
    if (editRemitterValues) {
      const mappedRemitter = mapToRemitterUpdateRequest(values, currentAccount?.id);
      const response = await RemittersService.updateRemitter(editRemitterValues.Id as string, mappedRemitter);
      if (response.status === 200) {
        refetchRemitters();
        form.resetForm();
        handleClose();
      }
    } else {
      const mappedRemitter = mapToRemitterCreateRequest(values, currentAccount?.id);
      const response = await RemittersService.createRemitter(mappedRemitter);
      if (response.status === 200) {
        refetchRemitters();
        form.resetForm();
        handleClose();
      }
    }
  };

  const form = useForm<IRemitterFormValues>(
    mapRemitterResponseToFormValues(editRemitterValues) ?? initialValues,
    validation,
    handleSubmit,
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      disableBackdropClick
    >
      <div style={{ width: '605px', padding: '32px' }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px', marginTop: '22px',
            }}
            >
              <div style={{
                color: '#1E8777',
                backgroundColor: '#F7F7F7',
                padding: '10px',
                borderRadius: '5px',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              >
                <FontAwesomeIcon
                  icon={editRemitterValues ? faExclamationCircle : faPaperPlane}
                  style={{ color: editRemitterValues ? '#CF752F' : '#1E8777' }}
                />
              </div>
              <div style={{ fontSize: '22px', fontWeight: 400 }}>
                {editRemitterValues ? t('edit_remitter') : t('create_new_remitter')}
              </div>
            </div>

            <div onClick={handleClose} role="button" tabIndex={0} className={styles.closeButton}>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ color: '#C3C3C3', fontSize: '16px', cursor: 'pointer' }}
              />
            </div>
          </div>
          <fieldset disabled={!!form.isSubmitting} style={{ border: 'none', padding: 0 }}>
            <BasicInformation
              form={form}
              countries={countries}
              currencies={currencies}
              editRemitterValues={editRemitterValues}
            />
            <div style={{ marginTop: '12px' }}>
              <BankDetailsSection form={form} editRemitterValues={editRemitterValues} countries={countries} />
            </div>
          </fieldset>
          <div style={{
            marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px',
          }}
          >
            <ActionButton
              style={{ background: '#F7F7F7', color: '#212529' }}
              onClick={handleClose}
            >
              {t('cancel')}
            </ActionButton>
            <ActionButton
              style={{ background: '#1E8777', width: '100%' }}
              onClick={() => form.handleSubmit()}
              disabled={form.isSubmitting || !form.isValid}
            >
              {form.isSubmitting ? (
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '16px' }} />
              ) : (
                <>
                  {editRemitterValues ? (
                    <>
                      <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} />
                      {t('save')}
                    </>
                  ) : t('create_remitter')}
                </>
              )}
            </ActionButton>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default RemitterDrawer;
